import {
  FunnelStatusTerminalOutcome,
  type FunnelStatus,
} from 'src/api/generated';
import type { ComponentVariant } from 'src/modules/shared/types/component.type';

const KEYWORD_VARIANT_MAP: {
  keywords: string[];
  variant: ComponentVariant;
}[] = [
  {
    keywords: [
      'ganado',
      'won',
      'win',
      'completed',
      'success',
      'cerrado',
      'closed',
      'finalizado',
      'finished',
      'aprobado',
      'approved',
      'vendido',
      'sold',
      'exitoso',
      'successful',
    ],
    variant: 'success',
  },
  {
    keywords: [
      'perdido',
      'lost',
      'cancelled',
      'canceled',
      'rechazado',
      'rejected',
      'fallido',
      'failed',
      'descartado',
      'discarded',
      'abandonado',
      'abandoned',
      'no interesado',
      'not interested',
    ],
    variant: 'danger',
  },
  {
    keywords: [
      'en progreso',
      'progress',
      'active',
      'open',
      'activo',
      'trabajando',
      'working',
      'procesando',
      'processing',
      'en curso',
      'ongoing',
      'iniciado',
      'started',
    ],
    variant: 'primary',
  },
  {
    keywords: [
      'seguimiento',
      'follow',
      'pending',
      'pendiente',
      'revision',
      'review',
      'evaluacion',
      'evaluation',
      'contacto',
      'contact',
      'llamada',
      'call',
      'reunion',
      'meeting',
      'calificar',
      'lead',
      'qualify',
      'qualification',
    ],
    variant: 'warning',
  },
  {
    keywords: [
      'espera',
      'hold',
      'paused',
      'pausado',
      'suspendido',
      'suspended',
      'diferido',
      'deferred',
      'postergado',
      'postponed',
      'en pausa',
      'standby',
    ],
    variant: 'secondary',
  },
];

const DEFAULT_VARIANT: ComponentVariant = 'primary';

const normalize = (value?: string | null) =>
  value
    ? value
        .toLocaleLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
    : '';

/**
 * Resolves a status surface variant, preferring explicit terminal classification.
 * @param statusName - Name used for the established nonterminal treatment.
 * @param terminalOutcome - Current terminal outcome, when classified.
 * @returns Green for positive, neutral gray for negative, or the name-based fallback.
 */
export const getStatusVariant = (
  statusName?: string | null,
  terminalOutcome?: FunnelStatus['terminalOutcome']
): ComponentVariant => {
  if (terminalOutcome === FunnelStatusTerminalOutcome.POSITIVE) {
    return 'success';
  }
  if (terminalOutcome === FunnelStatusTerminalOutcome.NEGATIVE) {
    return 'neutral';
  }
  const normalizedName = normalize(statusName);

  if (!normalizedName) {
    return DEFAULT_VARIANT;
  }

  for (const entry of KEYWORD_VARIANT_MAP) {
    if (
      entry.keywords.some((keyword) =>
        normalizedName.includes(normalize(keyword))
      )
    ) {
      return entry.variant;
    }
  }

  return DEFAULT_VARIANT;
};
